import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  checkLocationState,
  readFix,
  requestLocation,
  type Coords,
  type Fix,
  type LocationState
} from '@/lib/location';

/** 'checking' só existe até a primeira consulta responder. */
export type PositionStatus = 'checking' | LocationState;

export interface UserPosition {
  coords: Coords | null;
  /** Mesma posição de `coords`, com a incerteza (trava do ConfigureCharge). */
  fix: Fix | null;
  status: PositionStatus;
  /** Lendo a posição agora (permissão já dada, GPS buscando). */
  locating: boolean;
  /** Dispara o pedido do SO (só chamar de um gesto do usuário/onboarding). */
  request: () => Promise<PositionStatus>;
}

/**
 * Posição do usuário pra "estação mais próxima" e pro aviso insistente da
 * Home (bug 2026-10-08 — ver `lib/location.ts`).
 *
 * No mount NUNCA mostra pedido: só consulta o estado e, se já concedido, lê a
 * posição. O pedido sai exclusivamente via `request()` (onboarding, aviso da
 * Home, trava do ConfigureCharge).
 *
 * Reconsulta quando o app volta pro primeiro plano: é assim que o aviso some
 * sozinho depois que o cliente liga o GPS pelo painel rápido ou libera a
 * permissão nos Ajustes.
 *
 * `fresh`: aceita só posição de até 1 min (a da Home aceita 5) — pra julgar
 * se o cliente está no eletroposto AGORA.
 */
export function useUserPosition({ fresh = false }: { fresh?: boolean } = {}): UserPosition {
  const [fix, setFix] = useState<Fix | null>(null);
  const [status, setStatus] = useState<PositionStatus>('checking');
  const [locating, setLocating] = useState(false);
  // Os diálogos do sistema (permissão, "Ativar localização" do Google) pausam
  // a Activity e disparam `visibilitychange` no meio do pedido — a reconsulta
  // dessa volta não pode atropelar o resultado do pedido em andamento.
  const requesting = useRef(false);

  const sync = useCallback(async (isCancelled: () => boolean) => {
    if (requesting.current) return;
    const state = await checkLocationState();
    if (isCancelled() || requesting.current) return;
    setStatus(state);
    if (state !== 'granted') return;
    setLocating(true);
    const next = await readFix(fresh);
    if (isCancelled()) return;
    setLocating(false);
    if (next) setFix(next);
  }, [fresh]);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;
    void sync(isCancelled);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void sync(isCancelled);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [sync]);

  const request = useCallback(async (): Promise<PositionStatus> => {
    requesting.current = true;
    setLocating(true);
    try {
      const result = await requestLocation(fresh);
      setStatus(result.state);
      if (result.fix) setFix(result.fix);
      return result.state;
    } finally {
      requesting.current = false;
      setLocating(false);
    }
  }, [fresh]);

  // Identidade estável por leitura — o mapa e os useMemo da Home dependem dela.
  const coords = useMemo<Coords | null>(
    () => (fix ? { lat: fix.lat, lng: fix.lng } : null),
    [fix]
  );

  return { coords, fix, status, locating, request };
}
